// New-password policy (admin + developer). The BE is the final authority — it also
// rejects common passwords, the current password and passwords containing the username/email.

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRule {
  key: string;
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    key: 'length',
    label: `${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH && p.length <= PASSWORD_MAX_LENGTH,
  },
  { key: 'upper', label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'At least one digit', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'At least one special character', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export function isStrongPassword(password: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password));
}

/** Client-side checks for a change-password form. Returns an error message or null. */
export function validateNewPassword(params: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): string | null {
  const { currentPassword, newPassword, confirmPassword } = params;
  if (!currentPassword) return 'Please enter your current password.';
  if (!isStrongPassword(newPassword)) {
    return `New password must be ${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} characters and include uppercase, lowercase, a digit and a special character.`;
  }
  if (newPassword !== confirmPassword) return 'New password and confirmation do not match.';
  if (newPassword === currentPassword) return 'New password must be different from the current password.';
  return null;
}
